import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Space, Modal, Form, Input, Select, message, InputNumber, Tabs, Tag, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../../utils/axios';
import DepartmentSelect from '../../components/DepartmentSelect';
import PositionSelect from '../../components/PositionSelect';
import initManager from '../../utils/initManager';

const { Option } = Select;

const EmployeeTaskTemplatesSettings = () => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('basic');
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState(undefined);
  const [filterPos, setFilterPos] = useState(undefined);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(undefined);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      
      // 根据activeTab设置category参数
      if (activeTab !== 'all') {
        params.category = activeTab;
      }
      
      if (search) params.search = search;
      if (filterDept) params.department_id = filterDept;
      if (filterPos) params.position_id = filterPos;
      
      console.log('🔍 加载模板列表, 参数:', params);
      const res = await api.get('/employee-tasks/templates', { params });
      console.log('✅ 模板列表API响应:', res.data);
      if (res.data?.success) {
        setTemplates(res.data.data || []);
        console.log('📋 设置模板列表:', res.data.data?.length || 0, '条记录');
      } else {
        console.log('❌ API返回失败:', res.data);
      }
    } catch (e) {
      console.error('❌ 加载模板失败:', e);
      message.error('加载模板失败');
    } finally { setLoading(false); }
  };

  const loadAll = async () => {
    try {
      const res = await api.get('/employee-tasks/templates');
      if (res.data?.success) {
        const data = res.data.data || [];
        setAllTemplates(data);
        
        // 缓存数据并标记为已初始化
        initManager.setCachedData('employeeTaskTemplates', data);
        initManager.setInitialized('employeeTaskTemplates');
      }
    } catch (e) { /* 忽略 */ }
  };

  // 当activeTab改变时，清除不相关的筛选条件
  useEffect(() => {
    if (activeTab === 'basic') {
      setFilterDept(undefined);
      setFilterPos(undefined);
    } else if (activeTab === 'department') {
      setFilterPos(undefined);
    }
    // position和all标签页保持所有筛选条件
  }, [activeTab]);

  useEffect(() => { load(); }, [activeTab, search, filterDept, filterPos]);
  
  // 初始化时加载所有数据
  useEffect(() => {
    // 检查是否已初始化，避免重复获取数据
    if (!initManager.isInitialized('employeeTaskTemplates')) {
      loadAll();
    } else {
      // 使用缓存的数据
      const cachedData = initManager.getCachedData('employeeTaskTemplates');
      if (cachedData) {
        setAllTemplates(cachedData);
      } else {
        // 如果缓存为空，重新获取
        loadAll();
      }
    }
  }, []);

  const columns = [
    { title: '名称', dataIndex: 'name', key: 'name', width: 180 },
    { title: '描述', dataIndex: 'description', key: 'description', width: 260, ellipsis: true },
    { title: '分类', key: 'category', width: 120, render: (r) => {
      const isBasic = !r.department_id && !r.position_id;
      const isDept = !!r.department_id && !r.position_id;
      const color = isBasic ? 'blue' : isDept ? 'orange' : 'green';
      const text = isBasic ? '基础任务' : isDept ? '部门任务' : '岗位任务';
      return <Tag color={color}>{text}</Tag>;
    }},
    { title: '部门', key: 'department', width: 120, render: (r) => {
      if (!r.department_id) return '-';
      return r.department_name || `部门${r.department_id}`;
    }},
    { title: '岗位', key: 'position', width: 120, render: (r) => {
      if (!r.position_id) return '-';
      return r.position_name || `岗位${r.position_id}`;
    }},
    { title: '目标值', dataIndex: 'target_value', key: 'target_value', width: 90 },
    { title: '窗口期(天)', dataIndex: 'window_days', key: 'window_days', width: 110, 
      render: (v, r) => {
        if (!v) return '-';
        return `${v}天 (入职后${r.window_offset_days || 0}天开始)`;
      }
    },
    { title: '必做', dataIndex: 'required', key: 'required', width: 90, render: (v) => <Tag color={v? 'red':'default'}>{v ? '必做' : '可选'}</Tag> },
    { title: '验收', dataIndex: 'verification', key: 'verification', width: 110, render: (v) => {
      const map = { manual: { c: 'geekblue', t: '人工' }, auto: { c: 'green', t: '自动' }, attachment: { c: 'purple', t: '附件' } };
      const m = map[v] || { c: 'default', t: v };
      return <Tag color={m.c}>{m.t}</Tag>;
    }},
    {
      title: '操作', key: 'actions', width: 160, fixed: 'right', align: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => onEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除该模板？" onConfirm={() => onDelete(record)} okText="确定" cancelText="取消">
            <Button size="small" type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const onAdd = () => { setEditing(null); form.resetFields(); setModalVisible(true); };
  const onEdit = (record) => { setEditing(record); form.setFieldsValue(record); setModalVisible(true); };
  const onDelete = async (record) => {
    try {
      await api.delete(`/employee-tasks/templates/${record.id}`);
      message.success('删除成功');
      // 同时刷新当前列表和全部模板数据，确保标签页数字更新
      await Promise.all([load(), loadAll()]);
    } catch { message.error('删除失败'); }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log('🔍 提交表单数据:', values);
      if (editing) {
        console.log('📝 编辑模板:', editing.id);
        await api.put(`/employee-tasks/templates/${editing.id}`, values);
      } else {
        console.log('➕ 新增模板');
        const res = await api.post('/employee-tasks/templates', values);
        console.log('✅ 新增模板成功:', res.data);
      }
      message.success('保存成功');
      setModalVisible(false);
      console.log('🔄 刷新列表...');
      // 同时刷新当前列表和全部模板数据，确保标签页数字更新
      await Promise.all([load(), loadAll()]);
    } catch (e) {
      console.error('❌ 提交失败:', e);
    }
  };

  return (
    <div>
      <Card
        title="员工任务模板"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={async () => { await Promise.all([loadAll(), load()]); }}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>新增模板</Button>
          </Space>
        }
      >
        {/* Tab分类和筛选区域 */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'basic',
              label: (
                <span>
                  基础任务
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {allTemplates.filter(t => !t.department_id && !t.position_id).length}
                  </Tag>
                </span>
              )
            },
            {
              key: 'department',
              label: (
                <span>
                  部门任务
                  <Tag color="orange" style={{ marginLeft: 8 }}>
                    {allTemplates.filter(t => !!t.department_id && !t.position_id).length}
                  </Tag>
                </span>
              )
            },
            {
              key: 'position',
              label: (
                <span>
                  岗位任务
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    {allTemplates.filter(t => !!t.position_id).length}
                  </Tag>
                </span>
              )
            },
            {
              key: 'all',
              label: (
                <span>
                  全部模板
                  <Tag color="default" style={{ marginLeft: 8 }}>
                    {allTemplates.length}
                  </Tag>
                </span>
              )
            }
          ]}
        />

        {/* 筛选区域 */}
        <div style={{ marginBottom: 16, padding: '16px', background: '#fafafa', borderRadius: '6px' }}>
          <Space wrap>
            <span>搜索名称：</span>
            <Input 
              placeholder="搜索模板名称" 
              allowClear 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              style={{ width: 220 }} 
            />
            
            {activeTab === 'department' && (
              <>
                <span>部门筛选：</span>
                <DepartmentSelect 
                  allowClear 
                  value={filterDept} 
                  onChange={setFilterDept}
                  placeholder="选择部门筛选"
                  style={{ width: 200 }}
                />
              </>
            )}
            
            {activeTab === 'position' && (
              <>
                <span>部门筛选：</span>
                <DepartmentSelect 
                  allowClear 
                  value={filterDept} 
                  onChange={setFilterDept}
                  placeholder="选择部门筛选"
                  style={{ width: 200 }}
                />
                <span>岗位筛选：</span>
                <PositionSelect 
                  allowClear 
                  value={filterPos} 
                  onChange={setFilterPos} 
                  departmentId={filterDept}
                  placeholder="选择岗位筛选"
                  style={{ width: 200 }}
                />
              </>
            )}
            
            <Button 
              onClick={() => {
                setSearch('');
                setFilterDept(undefined);
                setFilterPos(undefined);
              }} 
              disabled={!search && !filterDept && !filterPos}
            >
              清除筛选
            </Button>
            
            <span style={{ color: '#666' }}>
              共 {templates.length} 条模板
              {filterDept && `（已筛选部门）`}
              {filterPos && `（已筛选岗位）`}
              {search && `（已搜索）`}
            </span>
          </Space>
        </div>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={templates}
          columns={columns}
          scroll={{ x: 1200 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
        />
      </Card>

      <Modal title={editing ? '编辑模板' : '新增模板'} open={modalVisible} onCancel={() => setModalVisible(false)} onOk={onSubmit}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={2} placeholder="任务的详细说明，如验收标准、示例" /></Form.Item>
          {/* 先选部门再选岗位，岗位组件依赖部门ID */}
          <Form.Item name="department_id" label="部门">
            <DepartmentSelect 
              allowClear 
              placeholder="请选择部门"
              onChange={(val) => {
                console.log('🔍 部门选择改变:', val);
                setSelectedDepartmentId(val);
                // 当部门改变时，重置岗位选项
                form.setFieldsValue({ position_id: undefined });
              }} 
            />
          </Form.Item>
          <Form.Item name="position_id" label="岗位">
            <PositionSelect 
              allowClear 
              departmentId={selectedDepartmentId}
              placeholder={selectedDepartmentId ? "请选择岗位" : "请先选择部门"}
              disabled={!selectedDepartmentId}
            />
          </Form.Item>
          <Form.Item name="window_days" label="窗口期(天)" extra="任务完成的时间窗口，从入职后开始计算"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
          <Form.Item name="required" label="是否必做" initialValue={1}><Select><Option value={1}>是</Option><Option value={0}>否</Option></Select></Form.Item>
          <Form.Item name="target_value" label="目标值（可选，量化任务如人数/次数）"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
          <Form.Item name="sort_order" label="排序" extra="数值越大优先级越高，列表越靠前"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmployeeTaskTemplatesSettings;


